import type { CSSProperties } from "react";
import { A } from "../assets";
import { Btn, Buddy, CONTACT, Eyebrow, Item, More, Page, Stat, href } from "../core";
import { BuddyChat, type Scenario } from "../demos";

const MORE_SCENARIOS: Scenario[] = [
  {
    id: "light", tab: "Something light",
    msgs: [
      { from: "guest", text: "What is the lightest gluten-free option that goes with a dry white wine?" },
      { from: "buddy", text: "The sea bream. It is grilled, dressed with citrus and fennel, and it is on the gluten-free list the kitchen has signed." },
      { from: "dish", name: "Sea bream, fennel, citrus", note: "Line-caught, Sicilian olive oil", chips: [{ t: "Gluten-free", kind: "ok" }, { t: "Light" }] },
      { from: "buddy", text: "With it, the Albariño by the glass: dry, a little saline, and it will not cover the fish." },
    ],
  },
  {
    id: "veg", tab: "Vegetarian",
    msgs: [
      { from: "guest", text: "I'm vegetarian and I love truffle. What would you recommend?" },
      { from: "buddy", text: "The truffle risotto is vegetarian as written. The chef finishes it at the pass with aged parmesan, so it does contain dairy." },
      { from: "guest", text: "Can it be made vegan?" },
      { from: "buddy", text: "I can't promise that for you. Vegan changes are the chef's decision, so I have asked your server to come over and confirm." },
      { from: "note", text: "Where the kitchen has not recorded an answer, Chef Buddy does not invent one." },
    ],
  },
  {
    id: "story", tab: "The chef's story",
    msgs: [
      { from: "guest", text: "What is the chef proud of tonight?" },
      { from: "buddy", text: "The lamb shoulder. It is braised for six hours with dried lime, a recipe from the chef's grandmother in Muscat, and it is carved at the table for two." },
      { from: "guest", text: "We'll take it." },
      { from: "buddy", text: "Good choice. One lamb shoulder for two is with the kitchen. It takes about 25 minutes, so I can suggest something small to start." },
    ],
  },
  {
    id: "room", tab: "From your room",
    msgs: [
      { from: "guest", text: "It's late. Anything warm and quick I can have in the room?" },
      { from: "buddy", text: "The kitchen's late menu is open until 2 am. The chicken broth with noodles is the quickest, about 20 minutes to your door." },
      { from: "guest", text: "Yes please, and a camomile tea." },
      { from: "buddy", text: "Ordered for Room 1408. The in-room dining team has it, and I will tell you when it is on its way." },
    ],
  },
];

export default function ChefBuddy() {
  return (
    <Page current="/chef-buddy" contact={{ title: <>Put Chef Buddy <em>on your tables.</em></>, lede: "Send us a menu. We will load it, sign off the allergen matrix with your chef, and show you Chef Buddy answering questions about your own dishes." }}>
      <section className="tk-hero tk-hero--bone" style={{ background: "linear-gradient(180deg, #FBE9D6 0%, var(--bone) 78%)" }}>
        <div className="tk-wrap tk-hero__grid">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Chef Buddy · for guests</Eyebrow>
              <h1 className="tk-h1">Ask the menu <em>anything.</em></h1>
            </div>
            <p className="tk-lede">Chef Buddy is the concierge inside the menu at your table or in your room. Ask what is light, what is safe for you, or what the chef is proud of tonight. It answers in your language and sends your order to the kitchen.</p>
            <div className="tk-row">
              <Btn to="#ask">Try a conversation</Btn>
              <Btn to="#operators" kind="ghost">I run a restaurant</Btn>
            </div>
            <ul className="tk-ticks">
              <li>No app to download</li>
              <li>60+ languages</li>
              <li>A person is always one tap away</li>
            </ul>
          </div>
          <div style={{ display: "grid", justifyItems: "center", paddingTop: 30 }}>
            <Buddy width="min(340px, 62%)" float style={{ marginRight: "22%" }}>
              <small>Chef Buddy</small>
              Good evening. مساء الخير. Bonsoir. 晚上好. What are you in the mood for?
            </Buddy>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--paper tk-sec--tight">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>How it works at your table</Eyebrow>
            <h2 className="tk-h2">Scan, ask, <em>order.</em></h2>
          </div>
          <ol className="tk-steps">
            <li><b>Scan the K</b><span>On the table card or the card in your room. It opens in your phone's browser. There is nothing to install and no account to create.</span></li>
            <li><b>Ask in your own words</b><span>Speak or type, in your language. Ask about ingredients, portions, spice, pairings, or simply what is good tonight.</span></li>
            <li><b>Order when you are ready</b><span>Your order goes to the kitchen and your server sees it at once. If you would rather talk to a person, one tap brings them over.</span></li>
          </ol>
        </div>
      </section>

      <section className="tk-sec tk-sec--ink" id="ask">
        <div className="tk-wrap tk-split">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Things guests ask</Eyebrow>
              <h2 className="tk-h2">It answers the way <em>the chef would.</em></h2>
            </div>
            <p className="tk-body">Chef Buddy is taught the restaurant's own menu: the ingredients, the techniques and the stories behind the dishes. Choose a question to see how a conversation goes.</p>
            <p className="tk-fine">These are example conversations on a sample menu. In a restaurant, Chef Buddy speaks only about that restaurant's dishes.</p>
          </div>
          <div style={{ display: "grid", justifyItems: "center" }}>
            <BuddyChat scenarios={MORE_SCENARIOS} place="Table 7" />
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--bone">
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>Three promises to every guest</Eyebrow>
            <h2 className="tk-h2">Helpful, and <em>careful where it counts.</em></h2>
          </div>
          <div className="tk-grid tk-grid--3">
            <Item icon="shield" title="It will not guess about allergies">Every allergen answer is checked against a matrix the kitchen has signed, dish by dish. Halal, kosher, Jain, vegan, vegetarian and gluten-free are handled the same way. If the answer is not recorded, Chef Buddy tells you so and brings your server.</Item>
            <Item icon="globe" title="It speaks your language">More than 60 languages, spoken or typed, including Arabic, Hindi, Mandarin, French and Spanish. You read and ask in yours. The kitchen cooks from theirs.</Item>
            <Item icon="heart" title="It remembers only what you allow">If you want your preferences kept for next time, you say so. You can ask for them to be forgotten whenever you like, and they are.</Item>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--sand">
        <div className="tk-wrap tk-split tk-split--wide-left">
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>Where you will meet Chef Buddy</Eyebrow>
              <h2 className="tk-h2">At the table, in the room, <em>by the pool.</em></h2>
            </div>
            <div className="tk-grid tk-grid--2">
              <Item icon="table" title="Restaurants">The whole menu, explained and ordered from your seat.</Item>
              <Item icon="bed" title="In-room dining">Order from the card in your room. No phone call, no waiting on hold.</Item>
              <Item icon="glass" title="Bars and lounges">Cocktails, provenance and what to drink when you are not drinking.</Item>
              <Item icon="pin" title="Pool, beach and serviced apartments">Service that reaches the sunbed and the residence.</Item>
            </div>
          </div>
          <div className="tk-photo" style={{ aspectRatio: "1 / 1", width: "100%", maxWidth: 460, justifySelf: "end" }}>
            <img src={A.photoTable} alt="A guest holding a phone at a candlelit restaurant table" loading="lazy" />
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--ink" id="operators" style={{ "--bg": "var(--ink)" } as CSSProperties}>
        <div className="tk-wrap">
          <div className="tk-head">
            <Eyebrow>For the house</Eyebrow>
            <h2 className="tk-h2">What Chef Buddy does <em>for the restaurant.</em></h2>
            <p className="tk-lede">A guest who understands the menu orders with more confidence. The floor gets time back at the peak, and the order arrives exactly as the guest gave it.</p>
          </div>
          <div className="tk-grid tk-grid--4">
            <Stat n="15–20%" basis="Pilot-observed">Higher average checks where Chef Buddy is on the table. Results vary by venue and menu.</Stat>
            <Item icon="users" title="Time back for the floor">Routine menu questions are answered at the table, so servers spend the peak on service.</Item>
            <Item icon="order" title="Orders as the guest gave them">Modifiers and allergies travel with the order to the kitchen ticket, seat by seat.</Item>
            <Item icon="eye" title="Your brand, your voice">Chef Buddy is taught your dishes and your chef's words. White-label presentation is available on Pro and Enterprise.</Item>
          </div>
          <div className="tk-row" style={{ marginTop: 40 }}>
            <Btn to={CONTACT}>Book a demo</Btn>
            <More to={href("/platform", "pricing")}>Included in Pro and Enterprise. An add-on for Essentials and Plus.</More>
          </div>
        </div>
      </section>

      <section className="tk-sec tk-sec--paper">
        <div className="tk-wrap tk-split tk-split--top">
          <div>
            <Eyebrow>Questions guests ask us</Eyebrow>
            <h2 className="tk-h2">Before you <em>scan.</em></h2>
          </div>
          <div className="tk-faq">
            <details><summary>Do I need to download an app?</summary><p>No. Chef Buddy opens in your phone's browser when you scan the code. In some rooms it is already on the screen or the tablet.</p></details>
            <details><summary>Can I still talk to a person?</summary><p>Always. One tap calls your server, and Chef Buddy does this itself whenever a question is one a person should answer.</p></details>
            <details><summary>What happens if Chef Buddy does not know?</summary><p>It says so. It never makes up an ingredient, an allergen answer or a price. It passes the question to the team.</p></details>
            <details><summary>What do you keep about me?</summary><p>Nothing personal unless you ask us to remember your preferences. If you do, you can have them deleted at any time.</p></details>
          </div>
        </div>
      </section>
    </Page>
  );
}
